import ApiRoutes from '~/services/api-routes.js'
import HttpClient from '~/services/http-client.js'
import { useToast } from 'vue-toastification'
import moment from 'moment'
import type { Place, PlaceResource } from '~/types/place'
import type { ElevatorReservation, PlaceReservation } from '~/types/reservations'

export const useReservationsStore = defineStore('reservationsStore', () => {
  const places = ref<Place[]>([])
  const place = ref<Place>()
  const resources = ref<PlaceResource[]>([])
  const reservationsMyAll = ref<PlaceReservation[]>([])
  const reservationsMyToday = ref<PlaceReservation[]>([])
  const reservationsOthersToday = ref<PlaceReservation[]>([])
  const isLoading = ref(false)
  const allElevatorReservations = ref<ElevatorReservation[]>([])
  const mineElevatorReservations = ref<ElevatorReservation[]>([])
  const othersElevatorReservations = ref<ElevatorReservation[]>([])
  const disabledElevatorRanges = ref<{ from: Date; to: Date }[]>([])
  const floor = ref(0)
  const reservationsAllToday = ref<PlaceReservation[]>([])
  const reservationsMyPast = ref<PlaceReservation[]>([])
  const blocks = ref([])
  const futureDays = ref([])
  const blockReservations = ref([])
  const logs = ref([])
  const timelineRange = ref(null)
  const timelineReservations = ref([])

  // ─────────────────────────────────────────────
  // PLACES
  // ─────────────────────────────────────────────
  const fetchPlaces = async () => {
    isLoading.value = true
    const response = await HttpClient.get(ApiRoutes.RESERVATIONS_GET_PLACES)

    if (response.ok) {
      places.value = response.data
    }
    isLoading.value = false
  }

  const fetchPlace = async (placeId: number): Promise<Place | null> => {
    const response = await HttpClient.get(
      ApiRoutes.RESERVATIONS_GET_PLACE(placeId)
    )
    if (response.ok) {
      place.value = response.data
      return response.data as Place
    }

    return null
  }

  // ─────────────────────────────────────────────
  // RESOURCES
  // ─────────────────────────────────────────────
  const fetchResources = async (placeId: number) => {
    const res = await HttpClient.get(ApiRoutes.PLACE_RESOURCES_GET(placeId))
    if (res.ok) {
      resources.value = res.data
    }
  }

  // ─────────────────────────────────────────────
  // RESERVATIONS - ADMIN
  // ─────────────────────────────────────────────
  const fetchReservationsAdmin = async (
    placeId: number,
    date: string,
    resourceId: number | null = null,
    userId: number | null = null
  ) => {
    const url = ApiRoutes.RESERVATIONS_ADMIN_GET(
      placeId,
      date,
      resourceId,
      userId
    )
    const response = await HttpClient.get(url)
    if (response.ok) {
      reservationsAllToday.value = response.data
    }
  }

  const deleteReservationAdmin = async (id: number) => {
    const response = await HttpClient.delete(
      ApiRoutes.RESERVATIONS_ADMIN_DELETE(id)
    )
    if (response.data.message) {
      useToast().success('Rezervace byla změněna.')
    }
  }

  const deleteReservationsAdmin = async (ids: number[] = []) => {
    if (ids.length === 0) return
    const url = ApiRoutes.RESERVATIONS_ADMIN_DELETE_MANY
      ? ApiRoutes.RESERVATIONS_ADMIN_DELETE_MANY(ids)
      : `/reservations/admin?${ids.map((i) => `ids=${i}`).join('&')}`

    const response = await HttpClient.delete(url)
    if (response.data.message) {
      useToast().success(response.data.message)
    }
  }

  const postReservationAdmin = async ({
                                        placeId,
                                        resourceId,
                                        datetimeFrom,
                                        datetimeTo,
                                        userId
                                      }: {
    placeId: number
    resourceId: number | null
    datetimeFrom: string
    datetimeTo: string
    userId: number
  }) => {
    const payload = { placeId, resourceId, datetimeFrom, datetimeTo, userId }
    const response = await HttpClient.post(
      ApiRoutes.RESERVATIONS_ADMIN_POST,
      payload
    )

    return response.data
  }

  // ─────────────────────────────────────────────
  // RESERVATIONS - USER
  // ─────────────────────────────────────────────
  const fetchMyAllReservations = async (
    placeId: number,
    resourceId: number | null = null
  ) => {
    const response = await HttpClient.get(
      ApiRoutes.RESERVATIONS_FUTURE(placeId, resourceId)
      // ApiRoutes.RESERVATIONS_ALL()
    )
    if (response.ok) {
      reservationsMyAll.value = response.data
    }
  }

  const fetchTodayReservations = async (
    placeId: number,
    date: string,
    resourceId: number | null = null
  ) => {
    const url = ApiRoutes.RESERVATIONS(placeId, date, resourceId)
    const response = await HttpClient.get(url)

    if (response.ok) {
      reservationsMyToday.value = response.data.me || []
      reservationsOthersToday.value = response.data.others || []
    }
  }

  const postReservations = async (data: any) => {
    const response = await HttpClient.post(ApiRoutes.RESERVATIONS_POST, {
      reservations: data
    })
    if (response.ok) {
      useToast().success('Vaše rezervace byla přidána.')
    }
    return response
  }

  const myMergedReservations = computed(() => {
    if (reservationsMyAll.value.length === 0) return []

    const sortedReservations = reservationsMyAll.value.toSorted(
      (a, b) =>
        new Date(a.datetimeFrom).getTime() - new Date(b.datetimeFrom).getTime()
    )
    const mergedReservations = []

    let current: PlaceReservation & { slotIds: number[] } = {
      ...sortedReservations[0]!,
      slotIds: [sortedReservations[0]!.id]
    }

    for (let i = 1; i < sortedReservations.length; i++) {
      const next = sortedReservations[i]!

      if (
        moment(current.datetimeTo).isSameOrAfter(
          moment(next.datetimeFrom).subtract(1, 'minute')
        )
      ) {
        current.datetimeTo = moment
          .max(moment(current.datetimeTo), moment(next.datetimeTo))
          .toISOString()
        current.slotIds.push(next.id)
      } else {
        mergedReservations.push({ ...current })
        current = { ...next, slotIds: [next.id] }
      }
    }

    mergedReservations.push({ ...current })
    return mergedReservations
  })

  const deleteReservation = async (reservationId: number) => {
    const response = await HttpClient.delete(
      ApiRoutes.RESERVATIONS_DELETE(reservationId)
    )
    if (response.ok) {
      useToast().success('Rezervace byla úspěšně odstraněna.')
    }
  }

  const deleteMergedReservation = async (slotIds: number[]) => {
    if (slotIds && slotIds.length > 0) {
      const queryString = slotIds.map((id) => `ids=${id}`).join('&')
      const response = await HttpClient.delete(
        ApiRoutes.RESERVATIONS_MERGED_DELETE(queryString)
      )
      if (response.ok) {
        useToast().success('Rezervace úspěšně odstraněna(y).')
        return true
      }
    } else {
      useToast().error('Chyba v parametrech.')
    }
  }

  const fetchFutureDaysReservations = async (queryString: string) => {
    const response = await HttpClient.get(
      ApiRoutes.RESERVATIONS_FUTURE_DAYS(queryString)
    )
    if (response.ok) {
      futureDays.value = response.data
    }
  }

  const fetchReservationsForBlock = async (blockId: number) => {
    const response = await HttpClient.get(
      ApiRoutes.RESERVATIONS_BLOCKED(blockId)
    )
    if (response.ok) {
      blockReservations.value = response.data
    }
  }

  // ─────────────────────────────────────────────
  // OPEN DOOR
  // ─────────────────────────────────────────────
  const openDoor = async (doorId: string) => {
    const response = await HttpClient.post(ApiRoutes.OPEN_DOOR, {
      doorId
    })
    if (response.ok) {
      useToast().success('Dveře otevřeny')
    }
  }

  // ─────────────────────────────────────────────
  // ELEVATOR RESERVATIONS
  // ─────────────────────────────────────────────
  const fetchElevatorReservations = async () => {
    isLoading.value = true
    const response = await HttpClient.get(ApiRoutes.ELEVATOR_GET_RESERVATIONS)

    if (response.ok) {
      mineElevatorReservations.value = response.data.mine || []
      othersElevatorReservations.value = response.data.others || []

      allElevatorReservations.value = [
        ...mineElevatorReservations.value,
        ...othersElevatorReservations.value
      ]

      disabledElevatorRanges.value = allElevatorReservations.value.map(
        (reservation) => ({
          from: new Date(reservation.reservedFrom),
          to: new Date(reservation.reservedTo)
        })
      )

      floor.value = response.data.floor || 0
    }
    isLoading.value = false
  }

  const postElevatorReservation = async (
    reservedAt: string,
    direction: 'arrival' | 'departure'
  ) => {
    const response = await HttpClient.post(
      ApiRoutes.ELEVATOR_POST_RESERVATION,
      {
        reservedAt,
        direction
      }
    )
    if (response.ok) {
      useToast().success('Vaše rezervace byla přidána.')
    }
  }

  const deleteElevatorReservation = async (reservationId: number) => {
    const response = await HttpClient.delete(
      ApiRoutes.ELEVATOR_DELETE_RESERVATION(reservationId)
    )
    if (response.ok) {
      useToast().success('Rezervace byla úspěšně odstraněna.')
    }
  }

  const elevatorLogs = async () => {
    isLoading.value = true
    const response = await HttpClient.get(ApiRoutes.ELEVATOR_LOGS)

    if (response.ok) {
      logs.value = response.data
    }
    isLoading.value = false
  }

  const elevatorTimeline = async ({ from, to }: { from?: string; to?: string } = {} = {}) => {
    isLoading.value = true
    const url = ApiRoutes.ELEVATOR_TIMELINE_LOGS({ from, to })
    const response = await HttpClient.get(url)

    if (response.ok) {
      timelineRange.value = response.data.range
      timelineReservations.value = response.data.reservations ?? []
    }

    isLoading.value = false
  }

  // ─────────────────────────────────────────────
  // BLOCKS
  // ─────────────────────────────────────────────
  const fetchBlocksAdmin = async (
    placeId: number,
    resourceId: number | null = null
  ) => {
    const url = ApiRoutes.BLOCKS_ADMIN_GET(placeId, resourceId)
    const response = await HttpClient.get(url)
    if (response.ok) {
      blocks.value = response.data
    }
  }

  const postBlockAdmin = async ({
                                  placeId,
                                  datetimeFrom,
                                  datetimeTo,
                                  resourceId = null
                                }: {
    placeId: number
    datetimeFrom: string
    datetimeTo: string
    resourceId?: number | null
  }) => {
    const payload = {
      datetimeFrom,
      datetimeTo,
      ...(resourceId && { resourceId })
    }
    const response = await HttpClient.post(
      ApiRoutes.BLOCKS_ADMIN_POST(placeId),
      payload
    )
    if (response.ok) {
      await fetchBlocksAdmin(placeId, resourceId)
    }
  }

  const deleteBlockAdmin = async (placeId: number, blockId: number) => {
    const response = await HttpClient.delete(
      ApiRoutes.BLOCKS_ADMIN_DELETE(placeId, blockId)
    )

    if (response.ok) {
      await fetchBlocksAdmin(placeId)
    }
  }

  return {
    isLoading,
    place,
    places,
    reservationsMyAll,
    reservationsMyToday,
    reservationsOthersToday,
    myMergedReservations,
    allElevatorReservations,
    mineElevatorReservations,
    othersElevatorReservations,
    disabledElevatorRanges,
    floor,
    reservationsAllToday,
    reservationsMyPast,
    resources,
    blocks,
    futureDays,
    blockReservations,
    logs,
    fetchPlaces,
    fetchPlace,
    fetchMyAllReservations,
    fetchTodayReservations,
    postReservations,
    deleteReservation,
    deleteMergedReservation,
    openDoor,
    postElevatorReservation,
    fetchElevatorReservations,
    deleteElevatorReservation,
    deleteReservationAdmin,
    deleteReservationsAdmin,
    postReservationAdmin,
    fetchResources,
    fetchReservationsAdmin,
    fetchBlocksAdmin,
    postBlockAdmin,
    deleteBlockAdmin,
    fetchFutureDaysReservations,
    fetchReservationsForBlock,
    elevatorLogs,
    elevatorTimeline,
    timelineRange,
    timelineReservations
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useReservationsStore, import.meta.hot))
}
